import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onPrev, onNext, lang = "ro" }) {
  const isOpen = index !== null && index !== undefined;

  useEffect(() => {
    if (!isOpen) return;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [isOpen, onClose, onPrev, onNext]);

  const alt = (img) => (typeof img.alt === "string" ? img.alt : img.alt?.[lang] || img.alt?.ro || "");

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-main/95 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button onClick={onClose} data-testid="lightbox-close" aria-label="Close"
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-terracotta transition-colors">
            <X size={20} />
          </button>
          <span className="absolute top-7 left-6 text-cream/60 text-sm font-serif italic" data-testid="lightbox-counter">
            {index + 1} / {images.length}
          </span>

          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="lightbox-prev" aria-label="Previous"
            className="absolute left-3 md:left-8 z-10 w-12 h-12 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-terracotta transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="lightbox-next" aria-label="Next"
            className="absolute right-3 md:right-8 z-10 w-12 h-12 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-terracotta transition-colors"
          >
            <ChevronRight size={24} />
          </button>

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl w-full px-14 md:px-20"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[index].src}
              alt={alt(images[index])}
              className="w-full max-h-[78vh] object-contain rounded-xl"
              data-testid="lightbox-image"
            />
            <figcaption className="mt-4 text-center text-cream/70 text-sm font-light" data-testid="lightbox-caption">
              {alt(images[index])}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
